import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiClient } from '../../services/apiClient';
import { useAuthContext } from '../../context/AuthContext';
import './NotificationBell.css';

const POLL_INTERVAL = 30000; // 30 seconds

export default function NotificationBell() {
    const { isAuthenticated } = useAuthContext();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const bellRef = useRef(null);

    const fetchNotifications = useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            const data = await apiClient.request('/api/metrics/users/me/notifications?limit=20');
            setNotifications(data.notifications || []);
            setUnreadCount(data.unreadCount || 0);
        } catch (err) {
            console.debug('Could not fetch notifications:', err.message);
        }
    }, [isAuthenticated]);

    // Initial fetch + polling
    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, POLL_INTERVAL);
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                dropdownRef.current && !dropdownRef.current.contains(e.target) &&
                bellRef.current && !bellRef.current.contains(e.target)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleDismiss = async (id, e) => {
        e.stopPropagation();
        try {
            await apiClient.request(`/api/metrics/users/me/notifications/${id}`, { method: 'DELETE' });
            setNotifications(prev => prev.filter(n => n._id !== id));
            setUnreadCount(prev => {
                const wasDismissedUnread = notifications.find(n => n._id === id && !n.read);
                return wasDismissedUnread ? Math.max(0, prev - 1) : prev;
            });
        } catch (err) {
            console.error('Error dismissing notification:', err);
        }
    };

    const handleDismissAll = async () => {
        try {
            await apiClient.request('/api/metrics/users/me/notifications/all', { method: 'DELETE' });
            setNotifications([]);
            setUnreadCount(0);
        } catch (err) {
            console.error('Error dismissing all notifications:', err);
        }
    };

    const handleMarkRead = async (id) => {
        try {
            await apiClient.request(`/api/metrics/users/me/notifications/${id}/read`, { method: 'PUT' });
            setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            console.error('Error marking notification read:', err);
        }
    };

    const getSeverityIcon = (severity) => {
        switch (severity) {
            case 'critical': return 'fas fa-exclamation-circle';
            case 'warning': return 'fas fa-exclamation-triangle';
            default: return 'fas fa-info-circle';
        }
    };

    const getTimeAgo = (dateStr) => {
        if (!dateStr) return '';
        const diff = Date.now() - new Date(dateStr).getTime();
        const minutes = Math.floor(diff / 60000);
        if (minutes < 1) return 'just now';
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        return `${days}d ago`;
    };

    if (!isAuthenticated) return null;

    return (
        <div className="notification-bell-wrapper">
            <button
                ref={bellRef}
                className={`notification-bell-btn ${unreadCount > 0 ? 'has-unread' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                title="Notifications"
            >
                <i className="fas fa-bell"></i>
                {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
            </button>

            {isOpen && (
                <div className="notification-dropdown" ref={dropdownRef}>
                    <div className="notification-dropdown-header">
                        <h4>Notifications</h4>
                        <div className="notification-header-actions">
                            {notifications.length > 0 && (
                                <button className="clear-all-btn" onClick={handleDismissAll} title="Dismiss all">
                                    Clear all
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="notification-dropdown-body">
                        {notifications.length === 0 ? (
                            <div className="notification-empty">
                                <i className="fas fa-check-circle"></i>
                                <p>No notifications</p>
                            </div>
                        ) : (
                            notifications.map(n => (
                                <div
                                    key={n._id}
                                    className={`notification-item ${!n.read ? 'unread' : ''} severity-${n.alert?.severity || 'info'}`}
                                    onClick={() => !n.read && handleMarkRead(n._id)}
                                >
                                    <div className="notification-item-icon">
                                        <i className={getSeverityIcon(n.alert?.severity)}></i>
                                    </div>
                                    <div className="notification-item-content">
                                        <p className="notification-item-message">{n.alert?.message || 'Usage alert'}</p>
                                        <span className="notification-item-time">{getTimeAgo(n.createdAt)}</span>
                                    </div>
                                    <button
                                        className="notification-dismiss-btn"
                                        onClick={(e) => handleDismiss(n._id, e)}
                                        title="Dismiss"
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
